import { Mic, MessageSquare, CreditCard, Search, Smartphone, Zap, Trash2, CheckCircle, XCircle, Mic2, AlertTriangle, Play, Clock, Phone, DollarSign } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import useModal from '../components/useModal';
import Segmented from '../components/Segmented';
import Loader from '../components/Loader';
import Integraciones from './Integraciones';

const TOOL_TIPOS = [
  { id: 'calendar', icon: '📅', label: 'Calendario',    desc: 'Google Calendar, Teams, Meet, Calendly' },
  { id: 'whatsapp', icon: null, label: 'WhatsApp',      desc: 'Chatwoot, Meta API, Twilio' },
  { id: 'pago',     icon: null, label: 'Link de pago',  desc: 'Wompi, PayU, Mercado Pago' },
  { id: 'crm',      icon: null, label: 'Consultar CRM', desc: 'HubSpot, Salesforce, Directus' },
  { id: 'sms',      icon: null, label: 'Enviar SMS',    desc: 'Twilio, AWS SNS' },
  { id: 'custom',   icon: null, label: 'Webhook propio', desc: 'Cualquier endpoint HTTP' },
];

const PARAM_SUGERIDOS = {
  calendar: ['fecha','hora','nombre_cliente','telefono','confirmacion'],
  whatsapp: ['mensaje','telefono','nombre_cliente'],
  pago:     ['monto','descripcion','telefono','nombre_cliente'],
  crm:      ['telefono','id_cliente','resultado'],
  sms:      ['mensaje','telefono'],
  custom:   ['dato1','dato2','resultado'],
};

export default function Config() {
  const [config, setConfig]     = useState(null);
  const [form, setForm]         = useState({ voz_tts: '', llm_provider: 'openai' });
  const [previewing, setPreviewing] = useState(null);

  const previewVoz = async (e, vozId) => {
    e.stopPropagation();
    if (!vozId) return;
    if (previewing === vozId) return;
    setPreviewing(vozId);
    try {
      const res = await api.get(`/config/preview-voz?voz_id=${vozId}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => { setPreviewing(null); URL.revokeObjectURL(url); };
      audio.onerror = () => setPreviewing(null);
      await audio.play();
    } catch (err) {
      setPreviewing(null);
      // Voces self-hosted requieren el pod encendido (503)
      if (err?.response?.status === 503) {
        modalAlert({ title: 'Motor de voz apagado', message: 'La voz clonada se sintetiza en tu servidor, que se enciende al lanzar una campaña. Intenta de nuevo en unos minutos o lanza una campaña.', type: 'info', icon: '🎤' });
      }
    }
  };
  const [vocesClone, setVocesClone] = useState([]);
  const [saved, setSaved]       = useState(false);
  const [tools, setTools]       = useState([]);
  const [tab, setTab]           = useState('voz');
  const [cloneFile, setCloneFile]   = useState(null);
  const [cloneNombre, setCloneNombre] = useState('');
  const [cloneUploading, setCloneUploading] = useState(false);
  const [clonePreviewUrl, setClonePreviewUrl] = useState(null);
  const [cloneError, setCloneError] = useState('');
  const [expandedTool, setExpandedTool] = useState(null);
  const [newTool, setNewTool]   = useState(null);
  const [newParam, setNewParam] = useState('');
  const [sipForm, setSipForm] = useState({ sip_server: '', sip_port: '5060', sip_username: '', sip_password: '', sip_prefix_movil: '', sip_prefix_fijo: '' });
  const [sipSaving, setSipSaving] = useState(false);
  const [sipVerif, setSipVerif] = useState(null);
  const [sipVerifying, setSipVerifying] = useState(false);

  const desconectarSip = async () => {
    if (!window.confirm('¿Desconectar la troncal SIP? Las llamadas saldrán por el proveedor predeterminado.')) return;
    try {
      await api.patch('/config/sip', { sip_server: '', sip_port: '5060', sip_username: '', sip_password: '' });
      setSipVerif({ status: 'error', mensaje: 'Troncal desconectada' });
      setSipForm({ sip_server: '', sip_port: '5060', sip_username: '', sip_password: '' });
    } catch { setSipVerif({ status: 'error', mensaje: 'Error al desconectar' }); }
  };

  const guardarSip = async () => {
    setSipSaving(true);
    try {
      await api.patch('/config/sip', sipForm);
      setSipVerif({ status: 'ok', mensaje: 'Configuración guardada. Verifica la conexión.' });
    } catch { setSipVerif({ status: 'error', mensaje: 'Error guardando configuración' }); }
    setSipSaving(false);
  };

  const verificarSip = async () => {
    setSipVerifying(true);
    setSipVerif(null);
    try {
      const r = await api.get('/config/sip/verificar');
      setSipVerif(r.data);
    } catch { setSipVerif({ status: 'error', mensaje: 'Error verificando conexión' }); }
    setSipVerifying(false);
  };

  const [pwdForm, setPwdForm] = useState({ password_actual: '', password_nueva: '', password_confirmar: '' });
  const [pwdMsg, setPwdMsg] = useState(null);
  const [pwdSaving, setPwdSaving] = useState(false);

  const cambiarPassword = async () => {
    if (!pwdForm.password_actual || !pwdForm.password_nueva) { setPwdMsg({ ok: false, msg: 'Completa todos los campos' }); return; }
    if (pwdForm.password_nueva !== pwdForm.password_confirmar) { setPwdMsg({ ok: false, msg: 'Las contraseñas no coinciden' }); return; }
    if (pwdForm.password_nueva.length < 6) { setPwdMsg({ ok: false, msg: 'Mínimo 6 caracteres' }); return; }
    setPwdSaving(true);
    try {
      await api.patch('/config/password', { password_actual: pwdForm.password_actual, password_nueva: pwdForm.password_nueva });
      setPwdMsg({ ok: true, msg: 'Contraseña actualizada correctamente' });
      setPwdForm({ password_actual: '', password_nueva: '', password_confirmar: '' });
    } catch(e) {
      setPwdMsg({ ok: false, msg: e.response?.data?.detail || 'Error actualizando contraseña' });
    }
    setPwdSaving(false);
  };

  const { modal, confirm, alert: modalAlert } = useModal();

  useEffect(() => {
    api.get('/config/agent').then(r => {
      setConfig(r.data);
      setForm({ voz_tts: r.data.voz_tts, llm_provider: r.data.llm_provider || 'openai' });
      setVocesClone(r.data.voces_clone || []);
      if (r.data.sip_trunk_activo) {
        setSipForm({ sip_server: r.data.sip_server || '', sip_port: r.data.sip_port || '5060', sip_username: r.data.sip_username || '', sip_password: '', sip_prefix_movil: r.data.sip_prefix_movil || '', sip_prefix_fijo: r.data.sip_prefix_fijo || '' });
        // Auto-verificar estado SIP al cargar
        api.get('/config/sip/verificar').then(rv => setSipVerif(rv.data)).catch(() => setSipVerif({ status: 'warning', mensaje: 'No se pudo verificar' }));
      }
    }).catch(() => {});
    api.get('/config/tools').then(r => setTools(r.data)).catch(() => {});
  }, []);

  const subirVoiceClone = async () => {
    if (!cloneFile) return;
    if (!cloneNombre.trim()) { setCloneError('Escribe un nombre para la voz'); return; }
    setCloneUploading(true);
    setCloneError('');
    try {
      const fd = new FormData();
      fd.append('file', cloneFile);
      fd.append('nombre', cloneNombre.trim());
      const r = await api.post('/config/voice-clone', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVocesClone(prev => [...prev, r.data]);
      setCloneFile(null);
      setCloneNombre('');
      setClonePreviewUrl(null);
      await modalAlert({ title: '✅ Voz guardada', message: `La voz "${r.data.nombre}" está lista para usar.`, type: 'success', confirmText: 'OK' });
    } catch(e) {
      setCloneError(e.response?.data?.detail || 'Error subiendo el archivo');
    } finally { setCloneUploading(false); }
  };

  const eliminarVoiceClone = async (voiceId, nombre) => {
    const ok = await confirm({ title: 'Eliminar voz', message: `¿Eliminar la voz "${nombre}"?`, type: 'danger', icon: '🗑️', confirmText: 'Eliminar', cancelText: 'Cancelar' });
    if (!ok) return;
    try {
      await api.delete(`/config/voice-clone/${voiceId}`);
      setVocesClone(prev => prev.filter(v => v.id !== voiceId));
    } catch { modalAlert({ title: 'Error', message: 'Error eliminando voz', type: 'danger', icon: '❌' }); }
  };

  const guardarVoz = async () => {
    try {
      await api.patch('/config/agent', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { modalAlert({ title: 'Error', message: 'Error guardando configuración', type: 'danger', icon: '❌' }); }
  };

  const toggleTool = async (tool) => {
    const updated = { ...tool, activa: !tool.activa };
    try {
      await api.patch(`/config/tools/${tool.id}`, updated);
      setTools(prev => prev.map(t => t.id === tool.id ? { ...t, activa: !t.activa } : t));
    } catch { modalAlert({ title: 'Error', message: 'Error actualizando herramienta', type: 'danger', icon: '❌' }); }
  };

  const guardarTool = async (tool) => {
    try {
      if (tool.id) {
        await api.patch(`/config/tools/${tool.id}`, tool);
        setTools(prev => prev.map(t => t.id === tool.id ? tool : t));
      } else {
        const r = await api.post('/config/tools', tool);
        setTools(prev => [...prev, r.data]);
        setNewTool(null);
      }
      setExpandedTool(null);
      await modalAlert({ title: 'Guardado', message: 'Herramienta guardada correctamente.', type: 'success', icon: '✅', confirmText: 'OK' });
    } catch { modalAlert({ title: 'Error', message: 'Error guardando herramienta', type: 'danger', icon: '❌' }); }
  };

  const eliminarTool = async (tool) => {
    const ok = await confirm({ title: 'Eliminar herramienta', message: `¿Eliminar "${tool.nombre}"?`, type: 'danger', icon: '🗑️', confirmText: 'Eliminar', cancelText: 'Cancelar' });
    if (!ok) return;
    try {
      await api.delete(`/config/tools/${tool.id}`);
      setTools(prev => prev.filter(t => t.id !== tool.id));
    } catch { modalAlert({ title: 'Error', message: 'Error eliminando herramienta', type: 'danger', icon: '❌' }); }
  };

  const agregarParam = (tool, param) => {
    if (!param.trim()) return;
    const params = [...(tool.parametros || [])];
    if (!params.includes(param.trim())) params.push(param.trim());
    if (tool.id) setTools(prev => prev.map(t => t.id === tool.id ? { ...t, parametros: params } : t));
    else setNewTool(prev => ({ ...prev, parametros: params }));
    setNewParam('');
  };

  const quitarParam = (tool, param) => {
    const params = (tool.parametros || []).filter(p => p !== param);
    if (tool.id) setTools(prev => prev.map(t => t.id === tool.id ? { ...t, parametros: params } : t));
    else setNewTool(prev => ({ ...prev, parametros: params }));
  };

  const renderToolEditor = (tool, isNew = false) => {
    const tipo = TOOL_TIPOS.find(t => t.id === tool.tipo) || TOOL_TIPOS[5];
    const sugeridos = PARAM_SUGERIDOS[tool.tipo] || [];
    const updateField = (field, val) => {
      if (isNew) setNewTool(prev => ({ ...prev, [field]: val }));
      else setTools(prev => prev.map(t => t.id === tool.id ? { ...t, [field]: val } : t));
    };

    return (
      <div style={{ background: 'var(--bg)', border: '1px solid var(--accent-soft-2)', borderRadius: 10, padding: 16, marginTop: 8 }}>
        <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Nombre interno</label>
            <input value={tool.nombre} onChange={e => updateField('nombre', e.target.value)}
              style={inputStyle} placeholder="ej: agendar_cita" />
            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>Sin espacios, en minúsculas</div>
          </div>
          <div>
            <label style={labelStyle}>Tipo</label>
            <select value={tool.tipo} onChange={e => {
                const nt = e.target.value;
                updateField('tipo', nt);
                if (nt === 'whatsapp' || nt === 'pago')
                  updateField('webhook_url', `https://api.innovabot.com.co/internal/ejecutar-tool/${nt}`);
              }} style={inputStyle}>
              {TOOL_TIPOS.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Descripción para el agente IA</label>
          <textarea value={tool.descripcion} onChange={e => updateField('descripcion', e.target.value)}
            rows={2} style={{ ...inputStyle, resize: 'vertical' }}
            placeholder="Ej: Usa esta herramienta cuando el cliente acepte agendar una cita. Captura fecha y hora confirmada." />
          <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>El agente usará esta descripción para saber cuándo ejecutar la herramienta</div>
        </div>

        {(tool.tipo === 'whatsapp' || tool.tipo === 'pago') ? (
          <div style={{ marginBottom: 12, background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>🔌</span>
            <span>Usa tu integración conectada de <b>{tool.tipo === 'whatsapp' ? 'WhatsApp' : 'Pagos'}</b>. No necesitas ninguna URL — conéctala en la pestaña <b>Integraciones</b>.</span>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Webhook URL</label>
            <input value={tool.webhook_url} onChange={e => updateField('webhook_url', e.target.value)}
              style={inputStyle} placeholder="https://n8n.tuempresa.com/webhook/mi-tool" />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Parámetros que capturará el agente</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {(tool.parametros || []).map(p => (
              <span key={p} onClick={() => quitarParam(tool, p)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', background: 'var(--accent-soft)',
                border: '1px solid var(--accent-soft-2)', borderRadius: 20,
                fontSize: 11, color: 'var(--accent)', cursor: 'pointer',
              }}>
                {p} <span style={{ fontSize: 13 }}>×</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newParam} onChange={e => setNewParam(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { agregarParam(tool, newParam); } }}
              style={{ ...inputStyle, flex: 1 }} placeholder="nombre_param (Enter para agregar)" />
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sugeridos.filter(s => !(tool.parametros || []).includes(s)).map(s => (
              <span key={s} onClick={() => agregarParam(tool, s)} style={{
                padding: '3px 9px', background: 'rgba(74,222,128,0.08)',
                border: '1px solid var(--success-soft)', borderRadius: 20,
                fontSize: 10, color: 'var(--success)', cursor: 'pointer',
              }}>+ {s}</span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => guardarTool(tool)} style={btnPrimaryStyle}>✓ Guardar herramienta</button>
          <button onClick={() => { setExpandedTool(null); setNewTool(null); }} style={btnSecStyle}>Cancelar</button>
        </div>
      </div>
    );
  };

  if (!config) return <Loader full />;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Inter,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Configuración del agente</h1>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>Personaliza tu agente de voz</p>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 24 }}>
        <Segmented
          options={[{value:'voz',label:'Voz'},{value:'tools',label:'Herramientas'},{value:'integraciones',label:'Integraciones'},{value:'info',label:'Plan'}, ...(config?.sip_trunk_activo ? [{value:'sip',label:'Troncal SIP'}] : [])]}
          value={tab} onChange={setTab} />
      </div>

      {/* TAB: VOZ */}
      {tab === 'voz' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Voces predefinidas */}
          <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Voces del agente</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 18 }}>Elige cómo sonará tu agente. Cada voz tiene un estilo y personalidad distintos.</div>
            <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { id: 'andrea', nombre: 'Andrea', genero: 'Femenina', origen: 'Colombia', desc: 'Voz femenina cálida y expresiva, con acento colombiano natural. Recomendada para cobranza y atención al cliente.', provider: 'qwen_esmeralda' },
                { id: 'esmeralda', nombre: 'Esmeralda', genero: 'Femenina', origen: 'Colombia', desc: 'Voz femenina clara y profesional, con acento colombiano. Tono sobrio que transmite confianza.', provider: 'qwen_esmeralda' },
              ].map(v => (
                <div key={v.id} onClick={() => {
                  const prov = v.provider || (v.id.startsWith('qwen_realtime_') ? 'qwen_realtime' : 'openai');
                  setForm(f => ({ ...f, voz_tts: v.id, llm_provider: prov }));
                }} style={{
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
                  border: form.voz_tts === v.id ? '1.5px solid var(--accent-solid)' : '1px solid var(--surface-2)',
                  background: form.voz_tts === v.id ? 'var(--accent-soft)' : 'var(--surface-3)',
                  position: 'relative',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: form.voz_tts === v.id ? 'var(--accent)' : 'var(--text)' }}>{v.nombre}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-3)', padding: '1px 7px', borderRadius: 20 }}>{v.genero}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-3)' }}>{v.origen}</span>
                    {form.voz_tts === v.id && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--accent-solid)', fontWeight: 700 }}>✓ Activa</span>}
                    {v.id && (
                      <button onClick={(e) => previewVoz(e, v.id)} title="Escuchar muestra" style={{
                        marginLeft: form.voz_tts === v.id ? 4 : 'auto',
                        background: previewing === v.id ? 'var(--success-soft)' : 'rgba(96,165,250,0.1)',
                        border: `1px solid ${previewing === v.id ? 'rgba(74,222,128,0.3)' : 'rgba(96,165,250,0.2)'}`,
                        color: previewing === v.id ? 'var(--success)' : 'var(--info)',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: previewing === v.id ? 'wait' : 'pointer',
                      }}>{previewing === v.id ? '...' : 'Escuchar'}</button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>{v.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Voces personalizadas */}
          <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--accent-soft)', borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Voces personalizadas</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.7 }}>
              Clona una voz real para que tu agente suene exactamente como quieres.<br/>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Duración ideal: </span>45–60 segundos de habla continua y natural.<br/>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Formato: </span>WAV o MP3, grabado con micrófono (no por teléfono ni WhatsApp).<br/>
              <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Ambiente: </span>cuarto silencioso, sin eco, sin música ni otras voces.<br/>
              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Evitar: </span>ruido de fondo, pausas largas, voz leída de forma robótica.<br/>
              <span style={{ color: 'var(--text-3)' }}>Las voces clonadas son almacenadas por Innovabot y son de uso exclusivo de tu cuenta.</span>
            </div>

            {vocesClone.length > 0 && (
              <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                {vocesClone.map(v => (
                  <div key={v.id} onClick={() => setForm(f => ({ ...f, voz_tts: v.id, llm_provider: 'qwen_esmeralda' }))} style={{
                    padding: '14px 16px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
                    border: form.voz_tts === v.id ? '1.5px solid var(--accent-solid)' : '1px solid var(--accent-soft-2)',
                    background: form.voz_tts === v.id ? 'var(--accent-soft)' : 'rgba(124,58,237,0.03)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 15 }}>🎤</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: form.voz_tts === v.id ? 'var(--accent)' : 'var(--text)' }}>{v.nombre}</span>
                      {form.voz_tts === v.id && <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--accent-solid)', fontWeight: 700 }}>✓ Activa</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 10, color: 'var(--accent-solid)' }}>Voz personalizada clonada</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={e => previewVoz(e, v.id)} title="Escuchar muestra" style={{
                        padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(96,165,250,0.2)',
                        background: previewing === v.id ? 'var(--success-soft)' : 'rgba(96,165,250,0.1)',
                        color: previewing === v.id ? 'var(--success)' : 'var(--info)', fontSize: 10, cursor: 'pointer'
                      }}>{previewing === v.id ? '♪ …' : '▶ Oír'}</button>
                      <button onClick={e => { e.stopPropagation(); eliminarVoiceClone(v.id, v.nombre); }} style={{
                        padding: '2px 7px', borderRadius: 5, border: '1px solid var(--danger-soft)',
                        background: 'rgba(248,113,113,0.06)', color: 'var(--danger)', fontSize: 10, cursor: 'pointer'
                      }}>✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uploader */}
            <div style={{ border: '1px dashed var(--surface-2)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12, fontWeight: 600 }}>+ Agregar nueva voz clonada</div>
              <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Nombre de la voz</label>
                  <input value={cloneNombre} onChange={e => setCloneNombre(e.target.value)}
                    style={inputStyle} placeholder="ej: Voz paisa femenina" />
                </div>
                <div>
                  <label style={labelStyle}>Archivo de audio (WAV / MP3)</label>
                  <input type="file" accept="audio/*" onChange={e => {
                    const f = e.target.files[0];
                    if (!f) return;
                    setCloneFile(f);
                    setClonePreviewUrl(URL.createObjectURL(f));
                    setCloneError('');
                  }} style={{ ...inputStyle, padding: '6px 8px', cursor: 'pointer' }} />
                </div>
              </div>
              {clonePreviewUrl && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Previsualización</label>
                  <audio controls src={clonePreviewUrl} style={{ width: '100%', height: 36 }} />
                </div>
              )}
              {cloneError && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>⚠️ {cloneError}</div>}
              <button onClick={subirVoiceClone} disabled={!cloneFile || cloneUploading} style={{
                ...btnPrimaryStyle,
                opacity: (!cloneFile || cloneUploading) ? 0.5 : 1,
                cursor: (!cloneFile || cloneUploading) ? 'not-allowed' : 'pointer'
              }}>
                {cloneUploading ? 'Procesando voz...' : '↑ Subir y activar voz'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={guardarVoz} style={btnPrimaryStyle}>Guardar cambios</button>
            {saved && <span style={{ fontSize: 13, color: 'var(--success)' }}>✓ Guardado</span>}
          </div>
        </div>
      )}

      {/* TAB: STACK IA */}

      {/* TAB: TOOLS */}
      {tab === 'tools' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                {tools.filter(t => t.activa).length} herramienta{tools.filter(t=>t.activa).length!==1?'s':''} activa{tools.filter(t=>t.activa).length!==1?'s':''}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>El agente las ejecuta automáticamente según el contexto de la llamada</div>
            </div>
            <button onClick={() => { setNewTool({ nombre:'', descripcion:'', webhook_url:'', parametros:[], activa:true, tipo:'custom' }); setExpandedTool(null); }} style={btnPrimaryStyle}>
              + Nueva herramienta
            </button>
          </div>

          {/* Nueva tool */}
          {newTool && (
            <div className="ic-card" style={{ background: 'var(--bg)', border: '1.5px dashed var(--accent-solid)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: 8 }}>Nueva herramienta</div>
              {renderToolEditor(newTool, true)}
            </div>
          )}

          {tools.length === 0 && !newTool && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)', border: '1px dashed var(--surface-2)', borderRadius: 12 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⚡</div>
              <div style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 8 }}>No hay herramientas configuradas</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Agrega webhooks para que el agente pueda agendar citas, enviar WhatsApp, generar pagos y más</div>
            </div>
          )}

          {tools.map(tool => {
            const tipo = TOOL_TIPOS.find(t => t.id === tool.tipo) || TOOL_TIPOS[5];
            const isExpanded = expandedTool === tool.id;
            return (
              <div key={tool.id} style={{ background: 'var(--glass)', WebkitBackdropFilter: 'blur(20px) saturate(1.5)', backdropFilter: 'blur(20px) saturate(1.5)', border: `1px solid ${tool.activa ? 'var(--accent-soft-2)' : 'var(--surface-2)'}`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: tool.activa ? 'var(--accent-soft)' : 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {tipo.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{tool.nombre || 'Sin nombre'}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-3)', padding: '2px 8px', borderRadius: 20 }}>{tipo.label}</span>
                      {tool.activa && <span style={{ fontSize: 10, color: 'var(--success)', background: 'var(--success-soft)', padding: '2px 8px', borderRadius: 20 }}>Activa</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                      {(tool.descripcion || '').slice(0, 80)}{tool.descripcion?.length > 80 ? '...' : ''}
                    </div>
                    {(tool.parametros || []).length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {tool.parametros.map(p => (
                          <span key={p} style={{ fontSize: 10, color: 'var(--accent-solid)', background: 'var(--accent-soft)', padding: '2px 8px', borderRadius: 20 }}>{p}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => setExpandedTool(isExpanded ? null : tool.id)} style={{
                      padding: '6px 12px', borderRadius: 7, border: '1px solid var(--surface-2)',
                      background: 'transparent', color: 'var(--text-3)', fontSize: 11, cursor: 'pointer',
                    }}>
                      {isExpanded ? 'Cerrar' : 'Editar'}
                    </button>
                    {/* Toggle activa */}
                    <div onClick={() => toggleTool(tool)} style={{
                      width: 36, height: 20, borderRadius: 10, position: 'relative',
                      cursor: 'pointer', background: tool.activa ? 'var(--accent-solid)' : 'var(--surface-2)',
                      transition: 'background 0.2s', flexShrink: 0,
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3,
                        left: tool.activa ? 19 : 3,
                        transition: 'left 0.2s',
                      }} />
                    </div>
                    <button onClick={() => eliminarTool(tool)} style={{
                      padding: '6px 10px', borderRadius: 7,
                      border: '1px solid var(--danger-soft)',
                      background: 'rgba(248,113,113,0.08)', color: 'var(--danger)',
                      fontSize: 11, cursor: 'pointer',
                    }}>✕</button>
                  </div>
                </div>
                {isExpanded && renderToolEditor(tool)}
              </div>
            );
          })}
        </div>
      )}

      {/* TAB: INFO */}
      {tab === 'integraciones' && <Integraciones />}

      {tab === 'sip' && config?.sip_trunk_activo && (
        <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--accent-soft)', borderRadius: 12, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Troncal SIP propia</div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: sipVerif?.status === 'ok' ? 'var(--success-soft)' : sipVerif?.status === 'error' ? 'var(--danger-soft)' : 'rgba(251,191,36,0.1)',
              border: `1px solid ${sipVerif?.status === 'ok' ? 'rgba(74,222,128,0.3)' : sipVerif?.status === 'error' ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.3)'}`,
              color: sipVerif?.status === 'ok' ? 'var(--success)' : sipVerif?.status === 'error' ? 'var(--danger)' : 'var(--warning)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }}/>
              {sipVerif?.status === 'ok' ? 'Conectada' : sipVerif?.status === 'error' ? 'Sin conexión' : 'Verificando...'}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 20 }}>Configura tu propio proveedor de telefonía SIP.</div>
          <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Servidor SIP</label>
              <input value={sipForm.sip_server} onChange={e => setSipForm(f => ({ ...f, sip_server: e.target.value }))}
                placeholder="ej: sip.4yes.com o 201.234.240.69"
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Puerto</label>
              <input value={sipForm.sip_port} onChange={e => setSipForm(f => ({ ...f, sip_port: e.target.value }))}
                placeholder="5060"
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Usuario</label>
              <input value={sipForm.sip_username} onChange={e => setSipForm(f => ({ ...f, sip_username: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Contraseña</label>
              <input type="password" value={sipForm.sip_password} onChange={e => setSipForm(f => ({ ...f, sip_password: e.target.value }))}
                placeholder="Deja vacío para no cambiar"
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Prefijo móviles</label>
              <input type="text" value={sipForm.sip_prefix_movil} onChange={e => setSipForm(f => ({ ...f, sip_prefix_movil: e.target.value }))}
                placeholder="ej: 19157"
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Prefijo fijos</label>
              <input type="text" value={sipForm.sip_prefix_fijo} onChange={e => setSipForm(f => ({ ...f, sip_prefix_fijo: e.target.value }))}
                placeholder="ej: 888857"
                style={{ width: '100%', padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={guardarSip} disabled={sipSaving} style={{ padding: '9px 20px', background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              {sipSaving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={desconectarSip} style={{ padding: '9px 20px', background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 9, fontSize: 13, cursor: 'pointer' }}>
              Desconectar
            </button>
            <button onClick={verificarSip} disabled={sipVerifying} style={{ padding: '9px 20px', background: 'var(--surface-3)', border: '1px solid var(--accent-solid)', color: 'var(--accent)', borderRadius: 9, fontSize: 13, cursor: 'pointer' }}>
              {sipVerifying ? 'Verificando...' : 'Verificar conexión'}
            </button>
            {sipVerif && (
              <span style={{
                fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20,
                color: sipVerif.status === 'ok' ? 'var(--success)' : sipVerif.status === 'warning' ? 'var(--warning)' : 'var(--danger)',
                background: sipVerif.status === 'ok' ? 'var(--success-soft)' : sipVerif.status === 'warning' ? 'rgba(251,191,36,0.1)' : 'var(--danger-soft)',
              }}>{sipVerif.mensaje}</span>
            )}
          </div>
        </div>
      )}

      {tab === 'info' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="r-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            {[
              { icon: '💰', label: 'Tarifa por minuto', value: `$${config?.tarifa_por_minuto || 290} COP`, color: 'var(--text)' },
              { icon: '📞', label: 'Llamadas simultáneas', value: `${config?.max_concurrentes || 3} llamadas`, color: 'var(--text)' },
              { icon: '🎙️', label: 'Voz activa', value: config?.voces?.find(v => v.id === config?.voz_tts)?.nombre || 'Andrea', color: 'var(--text)' },
            ].map((m,i) => (
              <div key={i} style={{ padding: '20px', background: 'var(--surface-3)', borderRadius: 12, border: '1px solid var(--surface-2)' }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>{m.icon}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em' }}>{m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '20px', background: 'var(--surface-3)', borderRadius: 12, border: '1px solid var(--surface-2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>¿Necesitas más capacidad?</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
              Contacta a tu asesor de Innovabot para aumentar el límite de llamadas simultáneas, cambiar tu tarifa o acceder a funciones avanzadas.
            </div>
            <a href="mailto:servicioalcliente@innovabot.com.co" style={{
              display: 'inline-block', marginTop: 12, padding: '8px 16px', borderRadius: 8,
              background: 'var(--accent-soft)', border: '1px solid var(--accent-soft-2)',
              color: 'var(--accent)', fontSize: 12, textDecoration: 'none', fontWeight: 600,
            }}>✉ Contactar soporte</a>
          </div>
        </div>
      )}

      <Modal {...modal} />
    </div>
  );
}

const inputStyle    = { width:'100%', padding:'9px 12px', background:'var(--surface-3)', border:'1px solid var(--surface)', borderRadius:8, color:'var(--text)', fontSize:13, outline:'none', fontFamily:'Inter,sans-serif' };
const labelStyle    = { fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:6 };
const btnPrimaryStyle = { background:'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border:'none', color:'#fff', padding:'9px 18px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer' };
const btnSecStyle   = { background:'transparent', border:'1px solid var(--surface-2)', color:'var(--text-3)', padding:'9px 18px', borderRadius:8, fontSize:12, cursor:'pointer' };
